// ============================================================================
// DRAWING COMMENTS - Pinned comments on drawings with threaded replies
// Date: January 2026
// ============================================================================

'use client';

import React, { useState, useRef, useEffect } from 'react';
import {
  MessageSquare, Send, Reply, MoreVertical, Edit2, Trash2,
  MapPin, X, Clock, CheckCheck, AlertCircle, ChevronDown,
  ChevronRight, User, AtSign
} from 'lucide-react';
import type { DrawingComment } from './types';

// ============================================================================
// TYPES
// ============================================================================

interface DrawingCommentsProps {
  comments: DrawingComment[];
  onAddComment: (comment: {
    content: string;
    parent_id?: string;
    position_x?: number;
    position_y?: number;
    mentioned_users?: number[];
  }) => void;
  onEditComment: (id: string, content: string) => void;
  onDeleteComment: (id: string) => void;
  onResolveComment: (id: string) => void;
  onNavigateToComment: (comment: DrawingComment) => void;
  currentUserId?: number;
  users?: { id: number; name: string; avatar?: string }[];
  placingComment?: boolean;
  onTogglePlacingComment?: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

interface CommentItemProps {
  comment: DrawingComment;
  replies: DrawingComment[];
  onEdit: (content: string) => void;
  onDelete: () => void;
  onResolve: () => void;
  onReply: (content: string) => void;
  onNavigate: () => void;
  canEdit: boolean;
  users?: { id: number; name: string; avatar?: string }[];
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatTimeAgo(date: string): string {
  const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

// ============================================================================
// COMMENT ITEM COMPONENT
// ============================================================================

function CommentItem({
  comment,
  replies,
  onEdit,
  onDelete,
  onResolve,
  onReply,
  onNavigate,
  canEdit,
  users = []
}: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [showReplies, setShowReplies] = useState(true);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [showMenu, setShowMenu] = useState(false);

  const handleSaveEdit = () => {
    if (editContent.trim()) {
      onEdit(editContent.trim());
      setIsEditing(false);
    }
  };

  const handleReply = () => {
    if (replyContent.trim()) {
      onReply(replyContent.trim());
      setReplyContent('');
      setShowReplyInput(false);
    }
  };

  // Highlight @mentions in content
  const renderContent = (content: string) => {
    const mentionRegex = /@\[([^\]]+)\]\((\d+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }
      parts.push(
        <span key={match.index} className="text-blue-400 font-medium">
          @{match[1]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  return (
    <div className={`border-b border-gray-800 ${comment.is_resolved ? 'opacity-60' : ''}`}>
      <div className="p-3">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-semibold text-white">
              {getInitials(comment.user_name || 'UN')}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-white text-sm">{comment.user_name}</span>
                <span className="text-xs text-gray-500">{formatTimeAgo(comment.created_at)}</span>
                {comment.position_x !== null && comment.position_y !== null && (
                  <button
                    onClick={onNavigate}
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <MapPin className="w-3 h-3" />
                    Pinned
                  </button>
                )}
                {comment.is_resolved && (
                  <span className="text-xs text-green-400 flex items-center gap-1">
                    <CheckCheck className="w-3 h-3" />
                    Resolved
                  </span>
                )}
              </div>

              {/* Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1 rounded hover:bg-white/10 text-gray-400"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                
                {showMenu && (
                  <div className="absolute right-0 top-full mt-1 w-36 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-1 z-20">
                    {!comment.is_resolved && (
                      <button
                        onClick={() => { onResolve(); setShowMenu(false); }}
                        className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2"
                      >
                        <CheckCheck className="w-4 h-4" />
                        Resolve
                      </button>
                    )}
                    {canEdit && (
                      <>
                        <button
                          onClick={() => { setIsEditing(true); setShowMenu(false); }}
                          className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => { onDelete(); setShowMenu(false); }}
                          className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Comment content */}
            {isEditing ? (
              <div className="mt-2">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full bg-gray-800 text-white text-sm rounded-lg px-3 py-2 border border-gray-700 resize-none"
                  rows={3}
                  autoFocus
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    onClick={() => { setIsEditing(false); setEditContent(comment.content); }}
                    className="px-3 py-1 text-sm text-gray-400 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-300 mt-1 whitespace-pre-wrap">
                {renderContent(comment.content)}
              </p>
            )}

            {/* Actions */}
            {!isEditing && !comment.parent_id && (
              <div className="flex items-center gap-3 mt-2">
                <button
                  onClick={() => setShowReplyInput(!showReplyInput)}
                  className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                >
                  <Reply className="w-3.5 h-3.5" />
                  Reply
                </button>
                {replies.length > 0 && (
                  <button
                    onClick={() => setShowReplies(!showReplies)}
                    className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                  >
                    {showReplies ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reply input */}
      {showReplyInput && !comment.parent_id && (
        <div className="px-3 pb-3 pl-14">
          <div className="flex gap-2">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write a reply..."
              className="flex-1 bg-gray-800 text-white text-sm rounded-lg px-3 py-2 border border-gray-700 resize-none"
              rows={2}
              autoFocus
            />
            <button
              onClick={handleReply}
              disabled={!replyContent.trim()}
              className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 self-end"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Replies */}
      {showReplies && replies.length > 0 && (
        <div className="pl-10 border-l-2 border-gray-800 ml-6">
          {replies.map(reply => (
            <CommentItem
              key={reply.id}
              comment={reply}
              replies={[]}
              onEdit={(content) => onEdit(content)}
              onDelete={() => onDelete()}
              onResolve={() => {}}
              onReply={() => {}}
              onNavigate={() => {}}
              canEdit={canEdit}
              users={users}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MENTION INPUT COMPONENT
// ============================================================================

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  users: { id: number; name: string; avatar?: string }[];
  placeholder?: string;
  autoFocus?: boolean;
}

function MentionInput({ value, onChange, onSubmit, users, placeholder, autoFocus }: MentionInputProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionFilter, setSuggestionFilter] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursor = e.target.selectionStart;
    onChange(newValue);
    setCursorPosition(cursor);

    // Check for @ mention
    const textBeforeCursor = newValue.slice(0, cursor);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      setShowSuggestions(true);
      setSuggestionFilter(mentionMatch[1].toLowerCase());
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectUser = (user: { id: number; name: string }) => {
    const textBeforeCursor = value.slice(0, cursorPosition);
    const textAfterCursor = value.slice(cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      const newTextBefore = textBeforeCursor.slice(0, mentionMatch.index) + `@[${user.name}](${user.id}) `;
      onChange(newTextBefore + textAfterCursor);
    }
    
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(suggestionFilter)
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="w-full bg-gray-800 text-white text-sm rounded-lg px-3 py-2 border border-gray-700 resize-none"
        rows={3}
      />
      
      {/* Mention suggestions */}
      {showSuggestions && filteredUsers.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-1 max-h-48 overflow-y-auto z-20">
          {filteredUsers.slice(0, 5).map(user => (
            <button
              key={user.id}
              onClick={() => handleSelectUser(user)}
              className="w-full px-3 py-2 text-left hover:bg-gray-700 flex items-center gap-2"
            >
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-semibold text-white">
                  {getInitials(user.name)}
                </span>
              </div>
              <span className="text-sm text-white truncate">{user.name}</span>
            </button>
          ))}
        </div>
      )}
      
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-gray-500">
          <AtSign className="w-3 h-3 inline" /> to mention, Enter to send
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DrawingComments({
  comments,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onResolveComment,
  onNavigateToComment,
  currentUserId,
  users = [],
  placingComment = false,
  onTogglePlacingComment,
  isOpen,
  onToggle
}: DrawingCommentsProps) {
  const [newComment, setNewComment] = useState('');
  const [filter, setFilter] = useState<'all' | 'unresolved' | 'resolved'>('unresolved');

  // Group comments by parent
  const rootComments = comments.filter(c => !c.parent_id);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_id) {
      if (!acc[c.parent_id]) acc[c.parent_id] = [];
      acc[c.parent_id].push(c);
    }
    return acc;
  }, {} as Record<string, DrawingComment[]>);

  // Filter comments
  const filteredComments = rootComments.filter(c => {
    if (filter === 'unresolved') return !c.is_resolved;
    if (filter === 'resolved') return c.is_resolved;
    return true;
  });

  const handleAddComment = () => {
    if (newComment.trim()) {
      // Extract mentions
      const mentionRegex = /@\[([^\]]+)\]\((\d+)\)/g;
      const mentionedUsers: number[] = [];
      let match;
      while ((match = mentionRegex.exec(newComment)) !== null) {
        mentionedUsers.push(parseInt(match[2]));
      }

      onAddComment({
        content: newComment.trim(),
        mentioned_users: mentionedUsers
      });
      setNewComment('');
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="absolute top-16 right-4 p-2 rounded-lg bg-gray-900/90 backdrop-blur text-white hover:bg-gray-800 flex items-center gap-2"
        title="Show Comments"
      >
        <MessageSquare className="w-5 h-5" />
        {comments.filter(c => !c.is_resolved && !c.parent_id).length > 0 && (
          <span className="w-5 h-5 rounded-full bg-red-500 text-xs flex items-center justify-center">
            {comments.filter(c => !c.is_resolved && !c.parent_id).length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="w-80 bg-gray-900 border-l border-gray-800 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-gray-400" />
          <span className="font-semibold text-white">Comments</span>
          <span className="text-xs text-gray-500 px-1.5 py-0.5 bg-gray-700 rounded">
            {filteredComments.length}
          </span>
        </div>
        <button onClick={onToggle} className="p-1 rounded hover:bg-white/10 text-gray-400">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex border-b border-gray-800">
        {(['all', 'unresolved', 'resolved'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`flex-1 py-2 text-sm capitalize ${
              filter === f
                ? 'text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Pin comment button */}
      {onTogglePlacingComment && (
        <div className="p-3 border-b border-gray-800">
          <button
            onClick={onTogglePlacingComment}
            className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm ${
              placingComment
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <MapPin className="w-4 h-4" />
            {placingComment ? 'Click on drawing to place comment' : 'Add pinned comment'}
          </button>
        </div>
      )}

      {/* Comments list */}
      <div className="flex-1 overflow-y-auto">
        {filteredComments.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No comments yet</p>
            <p className="text-gray-500 text-xs mt-1">
              Be the first to add a comment
            </p>
          </div>
        ) : (
          filteredComments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              replies={repliesByParent[comment.id] || []}
              onEdit={(content) => onEditComment(comment.id, content)}
              onDelete={() => onDeleteComment(comment.id)}
              onResolve={() => onResolveComment(comment.id)}
              onReply={(content) => onAddComment({ content, parent_id: comment.id })}
              onNavigate={() => onNavigateToComment(comment)}
              canEdit={!currentUserId || comment.user_id === currentUserId}
              users={users}
            />
          ))
        )}
      </div>

      {/* New comment input */}
      <div className="p-3 border-t border-gray-800">
        <MentionInput
          value={newComment}
          onChange={setNewComment}
          onSubmit={handleAddComment}
          users={users}
          placeholder="Add a comment..."
        />
        <button
          onClick={handleAddComment}
          disabled={!newComment.trim()}
          className="w-full mt-2 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" />
          Post Comment
        </button>
      </div>
    </div>
  );
}
